/* eslint-disable no-unused-expressions */
import React, { PureComponent } from 'react';
import './time-picker.scss';

interface SNGTimePickerProps {
  id: string;
  hour?: string;
  minute?: string;
  period?: 'am' | 'pm';
  onChange?: (value: { hour: string; minute: string; period: 'am' | 'pm' }) => void;
  isDisabled?: boolean;
  hourAriaLabel?: string;
  minuteAriaLabel?: string;
  periodAriaLabel?: string;
}

interface SNGTimePickerState {
  hour: number;
  minute: number;
  period: 'am' | 'pm';
}

class SNGTimePicker extends PureComponent<SNGTimePickerProps, SNGTimePickerState> {
  static displayName = 'SNGTimePicker';

  constructor(props: SNGTimePickerProps) {
    super(props);

    this.state = {
      hour: this.parseHour(props.hour),
      minute: this.parseMinute(props.minute),
      period: props.period || 'am'
    };
  }

  componentDidUpdate(prevProps: SNGTimePickerProps) {
    const { hour, minute, period } = this.props;

    if (hour !== prevProps.hour || minute !== prevProps.minute || period !== prevProps.period) {
      this.setState({
        hour: this.parseHour(hour),
        minute: this.parseMinute(minute),
        period: period || 'am'
      });
    }
  }

  parseHour = (hour?: string) => {
    const parsed = Number(hour);
    if (!parsed || parsed < 1 || parsed > 12) return 1;
    return parsed;
  };

  parseMinute = (minute?: string) => {
    const parsed = Number(minute);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 59) return 0;
    return parsed;
  };

  formatTwoDigits = (value: number) => String(value).padStart(2, '0');

  emitChange = (nextState: Partial<SNGTimePickerState>) => {
    const updatedState = {
      ...this.state,
      ...nextState
    };

    this.setState(updatedState, () => {
      this.props.onChange?.({
        hour: this.formatTwoDigits(updatedState.hour),
        minute: this.formatTwoDigits(updatedState.minute),
        period: updatedState.period
      });
    });
  };

  increaseHour = () => {
    const nextHour = this.state.hour === 12 ? 1 : this.state.hour + 1;
    this.emitChange({ hour: nextHour });
  };

  decreaseHour = () => {
    const nextHour = this.state.hour === 1 ? 12 : this.state.hour - 1;
    this.emitChange({ hour: nextHour });
  };

  increaseMinute = () => {
    const nextMinute = this.state.minute === 59 ? 0 : this.state.minute + 1;
    this.emitChange({ minute: nextMinute });
  };

  decreaseMinute = () => {
    const nextMinute = this.state.minute === 0 ? 59 : this.state.minute - 1;
    this.emitChange({ minute: nextMinute });
  };

  togglePeriod = () => {
    this.emitChange({
      period: this.state.period === 'am' ? 'pm' : 'am'
    });
  };

  handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.emitChange({ hour: Number(e.target.value) });
  };

  handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.emitChange({ minute: Number(e.target.value) });
  };

  renderHourOptions = () =>
    Array.from({ length: 12 }, (_, index) => {
      const hour = index + 1;
      const value = this.formatTwoDigits(hour);

      return (
        <option key={value} value={hour}>
          {value}
        </option>
      );
    });

  renderMinuteOptions = () =>
    Array.from({ length: 60 }, (_, index) => {
      const value = this.formatTwoDigits(index);

      return (
        <option key={value} value={index}>
          {value}
        </option>
      );
    });

  renderStepper = ({
    value,
    onIncrease,
    onDecrease,
    onSelectChange,
    ariaLabel,
    options
  }: {
    value: number;
    onIncrease: () => void;
    onDecrease: () => void;
    onSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    ariaLabel: string;
    options: React.ReactNode;
  }) => {
    const { isDisabled } = this.props;

    return (
      <div className="sng-time-picker__stepper">
        <button type="button" className="sng-time-picker__arrow-btn sng-time-picker__arrow-btn--up" onClick={onIncrease} disabled={isDisabled} aria-label={`Increase ${ariaLabel}`}>
          <span aria-hidden="true">⌃</span>
        </button>

        <select
          id={`time-picker-${this.props.id}-${ariaLabel.toLowerCase()}`}
          name={`time-${this.props.id}-${ariaLabel.toLowerCase()}`}
          className="sng-time-picker__select"
          value={value}
          onChange={onSelectChange}
          disabled={isDisabled}
          aria-label={ariaLabel}
        >
          {options}
        </select>

        <button
          type="button"
          className="sng-time-picker__arrow-btn sng-time-picker__arrow-btn--down"
          onClick={onDecrease}
          disabled={isDisabled}
          aria-label={`Decrease ${ariaLabel}`}
        >
          <span aria-hidden="true">⌄</span>
        </button>
      </div>
    );
  };

  render() {
    const { isDisabled, periodAriaLabel, hourAriaLabel, minuteAriaLabel } = this.props;
    const { hour, minute, period } = this.state;

    return (
      <div className="sng-time-picker">
        {this.renderStepper({
          value: hour,
          onIncrease: this.increaseHour,
          onDecrease: this.decreaseHour,
          onSelectChange: this.handleHourChange,
          ariaLabel: hourAriaLabel || 'Hour',
          options: this.renderHourOptions()
        })}

        <span className="sng-time-picker__separator">:</span>

        {this.renderStepper({
          value: minute,
          onIncrease: this.increaseMinute,
          onDecrease: this.decreaseMinute,
          onSelectChange: this.handleMinuteChange,
          ariaLabel: minuteAriaLabel || 'Minute',
          options: this.renderMinuteOptions()
        })}

        <button
          type="button"
          className="sng-time-picker__period-btn"
          onClick={this.togglePeriod}
          disabled={isDisabled}
          aria-label={periodAriaLabel || `Toggle time period, current ${period}`}
        >
          {period}
        </button>
      </div>
    );
  }
}

export default SNGTimePicker;
